/** @format */

import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import {
    SimCardServiceTypeConfigurationGet,
    SimCardServiceTypeConfigurationPost,
    SimCardServiceTypeConfigurationPut,
    SimCardServiceTypeItem,
} from '../../../../../interfaces/collections/SimCardServiceInterface';
import { ReactComponent as Cancel } from '../../../../../assets/themes/OPL/img/icons/Cancel.svg';
import GridItem from '../../../../../components/Grid/GridItem';
import I18n from '../../../../../components/Translate/I18n';
import Button from '@material-ui/core/Button';
import GridContainer from '../../../../../components/Grid/GridContainer';
import { ApiCollectionInterface } from '../../../../../interfaces/collections/ApiCollectionInterface';
import { SystemLanguageInterface } from '../../../../../modules/language/interfaces/SystemLanguageInterface';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../../../helpers/FormGeneral';
import { OplSimCardServiceTypeSmsMsg } from './OplSimCardServiceTypeSmsMsg';
import { PlatformResellerAvailableLanguagesInterface } from '../../../../../modules/language/interfaces/PlatformResellerAvailableLanguagesInterface';
import { configDefault } from '../../../../../core/config';
import { SYSTEM_LANGUAGE_ACTIONS } from '../../../../../modules/language/actions';
import { OplSimCardServiceTypeNaiSmsMsg } from './OplSimCardServiceTypeNaiSmsMsg';
import { OplSimCardServiceTypeServiceBosSmsMsg } from './OplSimCardServiceTypeServiceBosSmsMsg';

type SmsMessageWithSystemLanguageDialogType = {
    open: boolean;
    close: () => void;
    simCardServiceType: SimCardServiceTypeItem;
    platformResellerId: number;
    type: number;
};

interface LanguageToMap {
    languageName: string;
    languageId?: number | null;
    mapItem: SimCardServiceTypeConfigurationGet | SimCardServiceTypeConfigurationPost | SimCardServiceTypeConfigurationPut;
    id?: string | null;
    isPost: boolean;
}

export const SmsMessageWithSystemLanguageDialog: FC<SmsMessageWithSystemLanguageDialogType> = ({
    open,
    close,
    simCardServiceType,
    platformResellerId,
    type,
}): ReactElement => {
    const dispatch = useDispatch();
    const [id, setId] = useState(-1);
    const [systemLanguages, setSystemLanguages] = useState<ApiCollectionInterface<SystemLanguageInterface> | null>(null);
    const [simCardServiceTypeConfiguration, setSimCardServiceTypeConfiguration] = useState<ApiCollectionInterface<
        SimCardServiceTypeConfigurationGet
    > | null>(null);
    const [languageToMap, setLanguageToMap] = useState<Array<LanguageToMap> | null>(null);
    const [resellerAvailableLanguagesCollection, setResellerAvailableLanguagesCollection] = useState<ApiCollectionInterface<
        PlatformResellerAvailableLanguagesInterface
    > | null>(null);

    useEffect(() => {
        if (id !== simCardServiceType.id) {
            setId(simCardServiceType.id);
            setResellerAvailableLanguagesCollection(null);
            setSimCardServiceTypeConfiguration(null);
        }
    }, [id, simCardServiceType]);

    useEffect(() => {
        if (resellerAvailableLanguagesCollection === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_reseller_to_system_languages', configDefault.PLATFORM_API_PREFIX)),
                SYSTEM_LANGUAGE_ACTIONS.LIST,
                [{ name: 'platformResellerId', value: platformResellerId }],
                [],
                (data: ApiCollectionInterface<PlatformResellerAvailableLanguagesInterface>): void => {
                    setResellerAvailableLanguagesCollection(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, resellerAvailableLanguagesCollection, platformResellerId]);

    useEffect(() => {
        if (simCardServiceTypeConfiguration === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_type_configurations')),
                'no-dispatch',
                [
                    { name: 'simCardServiceType', value: simCardServiceType.id },
                    { name: 'platformReseller', value: platformResellerId },
                    { name: 'deleted', value: false },
                ],
                [{ name: 'id', value: 'asc' }],
                (data: ApiCollectionInterface<SimCardServiceTypeConfigurationGet>): void => {
                    setSimCardServiceTypeConfiguration(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [simCardServiceTypeConfiguration, dispatch, simCardServiceType, platformResellerId]);

    useEffect(() => {
        if (systemLanguages === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/system_languages')),
                'no-dispatch',
                [],
                [{ name: 'language', value: 'asc' }],
                (data: ApiCollectionInterface<SystemLanguageInterface>): void => {
                    setSystemLanguages(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [systemLanguages, dispatch]);

    useEffect(() => {
        if (systemLanguages && simCardServiceTypeConfiguration && resellerAvailableLanguagesCollection) {
            const obj: Array<LanguageToMap> = systemLanguages['hydra:member']
                .filter((systemLanguage) => {
                    return resellerAvailableLanguagesCollection['hydra:member'].some(
                        (resellerLanguage) => resellerLanguage.systemLanguageId === systemLanguage.id
                    );
                })
                .map((systemLanguage) => {
                    const matchingConfig = simCardServiceTypeConfiguration['hydra:member'].find(
                        (config) => config.systemLanguage === `/base-api/api/platform/system_languages/${systemLanguage.id}`
                    );

                    if (matchingConfig) {
                        return {
                            languageName: systemLanguage.languageCode,
                            mapItem: matchingConfig,
                            id: matchingConfig['@id'],
                            isPost: false,
                        };
                    } else {
                        return {
                            languageName: systemLanguage.languageCode,
                            languageId: systemLanguage.id,
                            mapItem: {
                                systemLanguage: systemLanguage.id?.toString(),
                                configuration: '{}',
                                simCardServiceType: simCardServiceType.id.toString(),
                            } as SimCardServiceTypeConfigurationPost,
                            isPost: true,
                        };
                    }
                });

            setLanguageToMap(obj);
        }
    }, [systemLanguages, simCardServiceTypeConfiguration, simCardServiceType, resellerAvailableLanguagesCollection]);

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth={true}
            onClose={close}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle>
                <GridContainer justifyContent={'space-between'}>
                    <GridItem xs={11}>
                        <h4>
                            <I18n trans={type === 2 ? 'prov:feature.sms_message_for_nai_configure' : 'prov:feature.sms_message'} />
                        </h4>
                    </GridItem>
                    <GridItem xs={1}>
                        <Button onClick={close} color="primary">
                            <Cancel width={24} height={24} />
                        </Button>
                    </GridItem>
                </GridContainer>
            </DialogTitle>
            <DialogContent>
                {type === 1 &&
                    languageToMap &&
                    languageToMap.map((map) => {
                        return (
                            <OplSimCardServiceTypeSmsMsg
                                key={'map_text_id_' + map.languageName}
                                simCardServiceType={simCardServiceType}
                                simCardServiceTypeConfiguration={map.mapItem}
                                languageName={map.languageName}
                                languageId={map.languageId}
                                id={map.id}
                                isPost={map.isPost}
                                reloadConfiguration={(): void => {
                                    setSimCardServiceTypeConfiguration(null);
                                    setLanguageToMap(null);
                                }}
                                platformResellerId={platformResellerId}
                            />
                        );
                    })}
                {type === 2 &&
                    languageToMap &&
                    languageToMap.map((map) => {
                        return (
                            <OplSimCardServiceTypeNaiSmsMsg
                                key={'map_text_id_' + map.languageName}
                                simCardServiceType={simCardServiceType}
                                simCardServiceTypeConfiguration={map.mapItem}
                                languageName={map.languageName}
                                languageId={map.languageId}
                                id={map.id}
                                isPost={map.isPost}
                                reloadConfiguration={(): void => {
                                    setSimCardServiceTypeConfiguration(null);
                                    setLanguageToMap(null);
                                }}
                                platformResellerId={platformResellerId}
                            />
                        );
                    })}
                {type === 3 &&
                    languageToMap &&
                    languageToMap.map((map) => {
                        return (
                            <OplSimCardServiceTypeServiceBosSmsMsg
                                key={'map_text_id_' + map.languageName}
                                simCardServiceType={simCardServiceType}
                                simCardServiceTypeConfiguration={map.mapItem}
                                languageName={map.languageName}
                                languageId={map.languageId}
                                id={map.id}
                                isPost={map.isPost}
                                reloadConfiguration={(): void => {
                                    setSimCardServiceTypeConfiguration(null);
                                    setLanguageToMap(null);
                                }}
                                platformResellerId={platformResellerId}
                            />
                        );
                    })}
            </DialogContent>
        </Dialog>
    );
};
